import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthModule';
import Player from './Player';
import HlsPlayer from './HlsPlayer';
import { pulinaMoviesUrl } from '../config/pulinaMoviesBase';
import { MessageCircle, X, Maximize2, RotateCcw } from 'lucide-react';
import ChatMessages from './ChatMessages';
import ChatInput from './ChatInput';
import { buildKuiskaaCandidates, parseKuiskaaMessage } from './chatKuiskaaHelpers';
import ChatInlinePlayer from './ChatInlinePlayer';
import { AvatarFallback, getEventAreaUnlockProductId } from './partials/profileHelpers';
import { getEventStartCountdownText } from './partials/EventStartCountdown';
import { useCreditProductUnlocked } from '../useStatisticsUnlock';
import EventAreaLockedNotice from './EventAreaLockedNotice';
import GameAnswerInlinePanel, { shouldCloseGameAnswerPanel } from './GameAnswerInlinePanel';

const API = process.env.REACT_APP_API_URL || '';

const BALL_SIZE = 80;
const SPEED = 2;

/** Compute event start time from eventDate + time. Accepts both eventDate and event_date. */
function getEventStartTime(ev) {
  const timeStr = ev?.time || '00:00';
  const [hh, mm = 0] = timeStr.split(':').map((x) => parseInt(x, 10) || 0);
  const d = new Date();
  const dateStr = ev?.eventDate ?? ev?.event_date;
  if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(String(dateStr))) {
    const [y, m, day] = String(dateStr).split('-').map((x) => parseInt(x, 10));
    d.setFullYear(y, m - 1, day);
  }
  d.setHours(hh, mm, 0, 0);
  return d;
}

function getEventEndTime(ev) {
  const start = getEventStartTime(ev);
  const durationMinutes = ev?.minutes != null ? Math.max(ev.minutes, 60) : 60;
  return new Date(start.getTime() + durationMinutes * 60 * 1000);
}

/** Finnish line for scheduled event end; null before start. */
function getEventEndsInHint(ev, nowMs) {
  const startMs = getEventStartTime(ev).getTime();
  if (nowMs < startMs) return null;
  const endMs = getEventEndTime(ev).getTime();
  const remMs = endMs - nowMs;
  if (remMs <= 0) return 'Tapahtuma on päättynyt';
  const remMin = Math.ceil(remMs / 60000);
  if (remMin < 60) {
    return `Tapahtuma loppuu ${remMin} minuutin kuluttua`;
  }
  const h = Math.max(1, Math.round(remMin / 60));
  return `Tapahtuma loppuu ${h} tunnin kuluttua`;
}

/** Levyraati: get playlist array from event. */
function getLevyraatiPlaylist(ev) {
  const raw = ev?.levyraatiPlaylist ?? ev?.levyraati_playlist;
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [];
}

/** Levyraati: stable key for feedback lookup (same format as server). */
function getLevyraatiTrackKey(track) {
  const author = (track?.track_author ?? '').toString();
  const name = (track?.track_name ?? '').toString();
  return `${author}|${name}`;
}

/** Levyraati: round duration to next full minute (seconds). */
function getLevyraatiRoundedDurationSeconds(track) {
  return Math.ceil((Number(track?.duration_seconds) || 0) / 60) * 60;
}

/** Levyraati: which track is scheduled to be playing at now (ms). Returns { index, track } or null. */
function getLevyraatiNowPlaying(ev, playlist, now = Date.now()) {
  const startsAt = getEventStartTime(ev);
  if (!startsAt || !playlist.length) return null;
  const startMs = startsAt.getTime();
  if (now < startMs) return null;
  let elapsedSeconds = (now - startMs) / 1000;
  for (let i = 0; i < playlist.length; i++) {
    const track = playlist[i];
    const dur = getLevyraatiRoundedDurationSeconds(track);
    if (elapsedSeconds < dur) return { index: i, track };
    elapsedSeconds -= dur;
  }
  return null;
}

/** Levyraati: how many tracks should be "active" based on schedule (their slot has started). */
function getLevyraatiPlayableCount(ev, playlist, now = Date.now()) {
  const startsAt = getEventStartTime(ev);
  if (!startsAt || !playlist.length) return 0;
  const startMs = startsAt.getTime();
  if (now < startMs) return 0;
  let elapsedSeconds = (now - startMs) / 1000;
  let started = 0;
  for (let i = 0; i < playlist.length; i++) {
    if (elapsedSeconds < 0) break;
    started += 1;
    const track = playlist[i];
    const dur = getLevyraatiRoundedDurationSeconds(track);
    elapsedSeconds -= dur;
  }
  return Math.max(0, Math.min(started, playlist.length));
}

/** Create a game container message; success/score and notification-type messages get removeAt so they fade and are removed. */
function createGameMessage(text) {
  const id = `gm-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const isScoreSuccess =
    /Oikein! Sait pisteen!|Voitit! Pisteet:/.test(text) ||
    (String(text).includes('Pisteet:') && /Oikein!|Voitit!/.test(text));
  const isVäärin = /Väärin!?/.test(text);
  const isAlreadySolved = /Tämä on jo ratkaistu/.test(text);
  const isNotification = /Et ole tapahtuman järjestäjä|Vastaus olisi ollut|Vastaus vastaanotettu/.test(text);
  const isEventStatus = /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(text);
  const removeAt = isEventStatus
    ? Date.now() + 3000
    : isNotification
      ? Date.now() + 1000
      : isVäärin
        ? Date.now() + 1000
        : (isScoreSuccess || isAlreadySolved)
          ? Date.now() + 2500
          : undefined;
  const shortFade = removeAt && (isNotification || isVäärin || isEventStatus);
  return { id, text, removeAt, shortFade: !!shortFade };
}

/** Derive a stable hex color from a string (e.g. author name) for avatar fallback. */
function stringToHex(str) {
  let h = 0;
  const s = (str || '').toString();
  for (let i = 0; i < s.length; i++) h = (h << 5) - h + s.charCodeAt(i);
  return Math.abs(h % 0xffffff).toString(16).padStart(6, '0');
}

/** DVD-style bouncing avatar circle when stream has not started or embed URL is not set. */
function EventLivestreamIdle({ event, isOwnEvent, now, showBall = true, children = null }) {
  const containerRef = useRef(null);
  const ballRef = useRef(null);
  const authorId = event?.authorId != null ? String(event.authorId) : (event?.createdByUserId || null);
  const displayName = event?.author || '?';
  const [useFallback, setUseFallback] = useState(!authorId);

  const countdownText = getEventStartCountdownText(event, now);

  useEffect(() => {
    if (!containerRef.current || !ballRef.current) return;
    let x = 0;
    let y = 0;
    let vx = SPEED;
    let vy = SPEED;
    let rafId;

    function tick() {
      const el = containerRef.current;
      const ball = ballRef.current;
      if (!el || !ball) return;
      const rect = el.getBoundingClientRect();
      const cw = rect.width;
      const ch = rect.height;
      x += vx;
      y += vy;
      if (x <= 0) {
        x = 0;
        vx = -vx;
      }
      if (x >= cw - BALL_SIZE) {
        x = cw - BALL_SIZE;
        vx = -vx;
      }
      if (y <= 0) {
        y = 0;
        vy = -vy;
      }
      if (y >= ch - BALL_SIZE) {
        y = ch - BALL_SIZE;
        vy = -vy;
      }
      ball.style.left = `${x}px`;
      ball.style.top = `${y}px`;
      rafId = requestAnimationFrame(tick);
    }

    rafId = requestAnimationFrame(tick);
    return () => {
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [event?.id]);

  const showCountdown = event?.eventType !== 'levyraati';
  return (
    <div className="EventLivestream-idle-wrap" ref={containerRef}>
      {showCountdown && (
        <div className="EventLivestream-idle-countdown" aria-live="polite">
          {countdownText}
          <span className="EventLivestream-idle-dots" aria-hidden="true">
            <span>.</span><span>.</span><span>.</span>
          </span>
        </div>
      )}
      {event?.description && (
        <div className="EventLivestream-idle-description" aria-hidden="true">
          {event.description}
        </div>
      )}
      {showBall && (
        <div
          ref={ballRef}
          className="EventLivestream-idle-ball"
          style={{ width: BALL_SIZE, height: BALL_SIZE }}
          aria-hidden
        >
          {useFallback ? (
            <AvatarFallback
              bubbleColorHex={stringToHex(displayName)}
              displayName={displayName}
              username={displayName}
              size="large"
              className="EventLivestream-idle-fallback"
            />
          ) : (
            <img
              src={`${API}/api/users/${encodeURIComponent(authorId)}/avatar`}
              alt=""
              className="EventLivestream-idle-avatar"
              onError={() => setUseFallback(true)}
            />
          )}
        </div>
      )}
      {children}
    </div>
  );
}


function parseJsonResponse(res) {
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    return res.text().then((text) => {
      throw new Error(
        text.startsWith('<!') ? 'Backend returned HTML. Is the API server running?' : text
      );
    });
  }
  return res.json();
}

function normalizeChatMessages(messages, currentUserId) {
  if (!Array.isArray(messages)) return [];
  const uid = currentUserId != null ? String(currentUserId) : null;
  return messages.map((m) => {
    if (uid != null && m.authorId != null) {
      return { ...m, isOwn: String(m.authorId) === uid };
    }
    return { ...m, isOwn: !!m.isOwn };
  });
}

function shortenReplyPreview(text, maxLen = 50) {
  if (typeof text !== 'string') return '';
  const t = text.trim();
  return t.length <= maxLen ? t : t.slice(0, maxLen) + '...';
}

export default function EventLivestreamPage({ eventId }) {
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingDraft, setEditingDraft] = useState('');
  const [replyingToMessage, setReplyingToMessage] = useState(null);
  const [lastSentMessage, setLastSentMessage] = useState('');
  const [authorMainView, setAuthorMainView] = useState('stream');
  const [chatExpanded, setChatExpanded] = useState(false);
  const [chatDocked, setChatDocked] = useState(false);
  const [externalChatWindowOpen, setExternalChatWindowOpen] = useState(false);
  const externalChatWindowRef = useRef(null);
  const [chatPosition, setChatPosition] = useState(() => {
    // Initialize position to bottom-right corner
    if (typeof window !== 'undefined') {
      return { 
        x: Math.max(0, window.innerWidth - 420), 
        y: Math.max(0, window.innerHeight - 620) 
      };
    }
    return { x: 0, y: 0 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [mutedAuthorKeys, setMutedAuthorKeys] = useState(() => {
    try {
      const raw = localStorage.getItem('chatMutedAuthors');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const chatInputRef = useRef(null);
  const editInputRef = useRef(null);
  const chatWindowRef = useRef(null);
  const [now, setNow] = useState(() => Date.now());
  const [showRatingPrompt, setShowRatingPrompt] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingReason, setRatingReason] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [ratingError, setRatingError] = useState(null);
  const ratingTimerRef = useRef(null);
  const [slashResponses, setSlashResponses] = useState([]);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [gameDisplayPrompt, setGameDisplayPrompt] = useState(null);
  const [gameQuestionStartedAt, setGameQuestionStartedAt] = useState(null);
  const questionTimerRef = useRef(null);
  const [gameOptions, setGameOptions] = useState(null);
  const [abcdLockedOption, setAbcdLockedOption] = useState(null);
  const [abcdRoundStartedAt, setAbcdRoundStartedAt] = useState(null);
  const [gameDisplayMessages, setGameDisplayMessages] = useState([]);
  const [gameVastaaPanelOpen, setGameVastaaPanelOpen] = useState(false);
  const [gameVastaaSubmitting, setGameVastaaSubmitting] = useState(false);
  const [gameStateEmoji, setGameStateEmoji] = useState(null);
  const emojiRoundEndedRef = useRef(null);
  const emojiRoundKeyClearedRef = useRef(null);
  const [joinStartTime, setJoinStartTime] = useState(null);
  const [hasSentTenMinuteDm, setHasSentTenMinuteDm] = useState(false);
  const keskusteluJoinLoginPostedRef = useRef(false);
  useEffect(() => {
    keskusteluJoinLoginPostedRef.current = false;
  }, [eventId]);
  const [hasSentEventStartMessages, setHasSentEventStartMessages] = useState(false);
  const [discoveredHasGame, setDiscoveredHasGame] = useState(false);
  const [, setGameDataReady] = useState(false);
  const [levyraatiFeedback, setLevyraatiFeedback] = useState({});
  const [levyraatiActiveIndex, setLevyraatiActiveIndex] = useState(0);
  const [levyraatiPlaying, setLevyraatiPlaying] = useState(false);
  const [levyraatiMuted, setLevyraatiMuted] = useState(false);
  const [levyraatiCurrentTime, setLevyraatiCurrentTime] = useState(0);
  const [levyraatiDuration, setLevyraatiDuration] = useState(0);
  const [levyraatiStreamUrl, setLevyraatiStreamUrl] = useState(null);
  const [levyraatiStreamLoading, setLevyraatiStreamLoading] = useState(false);
  const [levyraatiStarHover, setLevyraatiStarHover] = useState(null);
  const [levyraatiEnded, setLevyraatiEnded] = useState(false);
  const [levyraatiThankYouVisible, setLevyraatiThankYouVisible] = useState(false);
  const [levyraatiFadeOut, setLevyraatiFadeOut] = useState(false);
  const [levyraatiFeedbackModalOpen, setLevyraatiFeedbackModalOpen] = useState(false);
  const [levyraatiFeedbackModalTrack, setLevyraatiFeedbackModalTrack] = useState(null);
  const [levyraatiFeedbackDraft, setLevyraatiFeedbackDraft] = useState('');
  const [levyraatiFeedbackSubmitting, setLevyraatiFeedbackSubmitting] = useState(false);
  const [levyraatiFeedbackModalError, setLevyraatiFeedbackModalError] = useState(null);
  const levyraatiAudioRef = useRef(null);
  const areaUnlockProductId =
    event?.areaUnlockCreditProductId ?? getEventAreaUnlockProductId(event);
  const { loading: areaUnlockLoading, unlocked: areaUnlockPurchased } = useCreditProductUnlocked(
    user?.id,
    areaUnlockProductId,
  );
  const startsAt = event ? getEventStartTime(event).getTime() : 0;
  const isKeskusteluEvent = event?.eventType === 'keskustelu' || event?.eventType === 'stream';
  const isLivestreamEvent = event?.eventType === 'livestream';
  const streamWindow = event?.streamWindow;
  const streamPlaybackActive =
    event?.streamPlaybackActive ?? streamWindow?.playbackActive ?? false;
  const streamPublishActive =
    event?.streamPublishActive ?? streamWindow?.publishActive ?? false;
  const showStream = isLivestreamEvent
    ? streamPlaybackActive && !!event?.livestreamPlaybackUrl
    : !!event?.embedUrl;

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Start participation timer once event is actually ongoing (by time), regardless of embed visibility.
  // For keskustelu, post a login log so server can sum ≥10 min attendance for the review DM.
  useEffect(() => {
    if (!user || !eventId || !event) return;
    if (joinStartTime != null) return;
    const hasStarted = startsAt > 0 && now >= startsAt;
    if (!hasStarted) return;
    setJoinStartTime(Date.now());
    const isKeskustelu = event.eventType === 'keskustelu' || event.eventType === 'stream' || event.eventType === 'livestream';
    if (isKeskustelu && !keskusteluJoinLoginPostedRef.current) {
      keskusteluJoinLoginPostedRef.current = true;
      fetch(`${API}/api/events/${encodeURIComponent(event.id)}/logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login' }),
      }).catch(() => {});
    }
  }, [user, eventId, event, startsAt, now, joinStartTime]);

  // When the event starts, ask backend to post welcome (once)
  useEffect(() => {
    if (!event || !eventId) return;
    if (hasSentEventStartMessages) return;
    const hasStarted = startsAt > 0 && now >= startsAt;
    if (!hasStarted) return;
    fetch(`${API}/api/events/${encodeURIComponent(event.id)}/start-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
      .catch(() => {})
      .finally(() => {
        setHasSentEventStartMessages(true);
      });
  }, [event, eventId, startsAt, now, hasSentEventStartMessages]);

  const eventStarted = startsAt > 0 && now >= startsAt;
  const eventEndsInHint = event ? getEventEndsInHint(event, now) : null;
  const eventHasEnded = !!(event && now >= getEventEndTime(event).getTime());
  const emojiGameEmojiRowClass =
    'EventLivestream-game-emoji-row' + (eventHasEnded ? ' EventLivestream-game-emoji-row--ended' : '');
  const isStreamEvent = isKeskusteluEvent;
  const isGameEvent = event?.eventType === 'game';
  const isLevyraatiEvent = event?.eventType === 'levyraati';
  const isMovieEvent = event?.eventType === 'movie';
  const isEmojiGame = event?.gameType === 'emoji-fi';
  const isAbcdGame = event?.gameType === 'abcd-fi';
  const isRiddleOrTriviaGame = event?.gameType === 'riddle-fi' || event?.gameType === 'trivia-fi';
  const eventHasGame = isGameEvent && !!(event?.hasGame || event?.gameType);
  const showGameContainer = isGameEvent && eventStarted;
  const showLevyraatiContainer = isLevyraatiEvent && eventStarted;
  const isOwnEvent = !!(event?.createdByUserId && user?.id != null && String(event.createdByUserId) === String(user.id));
  const showEmbeddedKeskusteluChat =
    ((isStreamEvent && eventStarted) ||
      (isLivestreamEvent && isOwnEvent && authorMainView === 'chat' && eventStarted)) &&
    !eventHasGame;
  const streamSrc = isLivestreamEvent ? event?.livestreamPlaybackUrl : event?.embedUrl;
  const showDockableChat = isLivestreamEvent || !isStreamEvent;
  const levyraatiPlaylist = getLevyraatiPlaylist(event);
  const levyraatiNowPlaying = showLevyraatiContainer ? getLevyraatiNowPlaying(event, levyraatiPlaylist, now) : null;
  const levyraatiPlayableCount = useMemo(
    () => getLevyraatiPlayableCount(event, levyraatiPlaylist, now),
    [event, levyraatiPlaylist, now],
  );

  const levyraatiAllRatedAndFeedbacked = useMemo(() => {
    if (!levyraatiPlaylist.length || levyraatiPlayableCount === 0) return false;
    return levyraatiPlaylist.slice(0, levyraatiPlayableCount).every((t) => {
      const f = levyraatiFeedback[getLevyraatiTrackKey(t)];
      return f?.rating != null && f?.feedback != null;
    });
  }, [levyraatiPlaylist, levyraatiFeedback, levyraatiPlayableCount]);

  const levyraatiGivenRatingsCount = useMemo(() => {
    if (!levyraatiPlaylist.length || levyraatiPlayableCount === 0) return 0;
    let count = 0;
    for (const t of levyraatiPlaylist.slice(0, levyraatiPlayableCount)) {
      const f = levyraatiFeedback[getLevyraatiTrackKey(t)];
      if (f?.rating != null) count++;
    }
    return count;
  }, [levyraatiPlaylist, levyraatiFeedback, levyraatiPlayableCount]);

  const levyraatiGivenFeedbackCount = useMemo(() => {
    if (!levyraatiPlaylist.length || levyraatiPlayableCount === 0) return 0;
    let count = 0;
    for (const t of levyraatiPlaylist.slice(0, levyraatiPlayableCount)) {
      const f = levyraatiFeedback[getLevyraatiTrackKey(t)];
      if (f?.feedback != null && f.feedback !== '') count++;
    }
    return count;
  }, [levyraatiPlaylist, levyraatiFeedback, levyraatiPlayableCount]);

  const levyraatiFirstIncompleteIndex = useMemo(() => {
    if (!levyraatiPlaylist.length) return null;
    for (let i = 0; i < levyraatiPlaylist.length; i++) {
      const t = levyraatiPlaylist[i];
      const f = levyraatiFeedback[getLevyraatiTrackKey(t)];
      const hasRating = f?.rating != null;
      const hasFeedback = f?.feedback != null && f.feedback !== '';
      if (!(hasRating && hasFeedback)) {
        return i;
      }
    }
    return null;
  }, [levyraatiPlaylist, levyraatiFeedback]);

  // Levyraati: sync active track index from schedule when now-playing changes.
  // Never move backwards: only advance when schedule index is ahead of current.
  useEffect(() => {
    if (!showLevyraatiContainer || levyraatiNowPlaying?.index == null) return;
    setLevyraatiActiveIndex((prev) => {
      if (levyraatiNowPlaying.index > prev) return levyraatiNowPlaying.index;
      return prev;
    });
  }, [showLevyraatiContainer, levyraatiNowPlaying?.index]);

  // Levyraati: fetch stream URL for active track (Audius only)
  useEffect(() => {
    if (!showLevyraatiContainer || !levyraatiPlaylist.length) {
      setLevyraatiStreamUrl(null);
      return;
    }
    const idx = Math.max(0, Math.min(levyraatiActiveIndex, levyraatiPlaylist.length - 1));
    const track = levyraatiPlaylist[idx];
    const source = (track?.source ?? '').toLowerCase();
    const url = (track?.url ?? '').trim();
    if (source !== 'audius' || !url) {
      setLevyraatiStreamUrl(null);
      return;
    }
    let cancelled = false;
    setLevyraatiStreamLoading(true);
    setLevyraatiStreamUrl(null);
    setLevyraatiCurrentTime(0);
    setLevyraatiDuration(0);
    fetch(`${API}/api/levyraati/stream-url?url=${encodeURIComponent(url)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data?.streamUrl) setLevyraatiStreamUrl(data.streamUrl);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLevyraatiStreamLoading(false);
      });
    return () => { cancelled = true; };
  }, [showLevyraatiContainer, levyraatiPlaylist, levyraatiActiveIndex]);

  // Personal score (all game types) – show when user is not TOP1
  const baseScores =
    event?.gameScores && typeof event.gameScores === 'object'
      ? event.gameScores
      : null;
  const emojiScores =
    isEmojiGame &&
    gameStateEmoji &&
    gameStateEmoji.scores &&
    typeof gameStateEmoji.scores === 'object'
      ? gameStateEmoji.scores
      : null;
  const allScores = emojiScores || baseScores;
  const personalScore =
    allScores && user?.id != null
      ? allScores[String(user.id)] ?? null
      : null;
  const showPersonalScore =
    personalScore != null &&
    (event.gameHighScoreValue == null || personalScore < event.gameHighScoreValue);

  // Reset discovered game and game-data readiness when switching to another event
  useEffect(() => {
    setDiscoveredHasGame(false);
    setGameDataReady(false);
  }, [eventId]);

  // Levyraati: fetch current user's feedback for "given" state per track
  useEffect(() => {
    if (!showLevyraatiContainer || !eventId || !user?.id) return;
    let cancelled = false;
    const includeHidden = event?.isHidden ? '&includeHidden=1' : '';
    fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-feedback?userId=${encodeURIComponent(user.id)}${includeHidden}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data != null && typeof data === 'object') {
          setLevyraatiFeedback((prev) => ({ ...prev, ...data }));
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showLevyraatiContainer, eventId, user?.id, event?.isHidden]);

  const closeLevyraatiFeedbackModal = useCallback(() => {
    setLevyraatiFeedbackModalOpen(false);
    setLevyraatiFeedbackModalTrack(null);
    setLevyraatiFeedbackDraft('');
    setLevyraatiFeedbackModalError(null);
    setLevyraatiFeedbackSubmitting(false);
  }, []);

  const submitLevyraatiFeedbackModal = useCallback(() => {
    if (!user?.id || !eventId || !levyraatiFeedbackModalTrack) return;
    const trimmed = levyraatiFeedbackDraft.trim();
    if (!trimmed) return;
    const track = {
      track_author: levyraatiFeedbackModalTrack.track_author,
      track_name: levyraatiFeedbackModalTrack.track_name,
    };
    const key = levyraatiFeedbackModalTrack.key;
    const existing = levyraatiFeedback[key];
    const nextFeedback = trimmed.slice(0, 2000);
    const includeHiddenParam = event?.isHidden ? '?includeHidden=1' : '';
    setLevyraatiFeedbackSubmitting(true);
    setLevyraatiFeedbackModalError(null);
    fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-feedback${includeHiddenParam}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: user.id,
        track_author: track.track_author,
        track_name: track.track_name,
        rating: existing?.rating ?? null,
        feedback: nextFeedback,
      }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('fail'))))
      .then(() => {
        const includeHidden = event?.isHidden ? '&includeHidden=1' : '';
        return fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-feedback?userId=${encodeURIComponent(user.id)}${includeHidden}`)
          .then((r) => (r.ok ? r.json() : null))
          .then((fb) => {
            if (fb != null && typeof fb === 'object') {
              setLevyraatiFeedback((prev) => ({ ...prev, ...fb }));
            }
          });
      })
      .then(() => {
        closeLevyraatiFeedbackModal();
      })
      .catch(() => {
        setLevyraatiFeedbackModalError('Lähetys epäonnistui. Yritä uudelleen.');
      })
      .finally(() => {
        setLevyraatiFeedbackSubmitting(false);
      });
  }, [
    user?.id,
    eventId,
    levyraatiFeedbackModalTrack,
    levyraatiFeedbackDraft,
    levyraatiFeedback,
    event?.isHidden,
    closeLevyraatiFeedbackModal,
  ]);

  // When event has started and API didn't say hasGame: discover game by fetching next-question so we show game container (even when event has embedUrl)
  useEffect(() => {
    if (!eventId || !event || !eventStarted || eventHasGame || discoveredHasGame) return;
    let cancelled = false;
    fetch(`${API}/api/events/${encodeURIComponent(eventId)}/next-question`)
      .then((res) => (res.ok ? res.json() : { prompt: null, noGame: true }))
      .then((data) => {
        if (cancelled) return;
        if (data?.prompt || data?.noGame === false) {
          setDiscoveredHasGame(true);
          setGameDataReady(true);
          if (data?.prompt) setGameDisplayPrompt(data.prompt);
          if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eventId, event, eventStarted, eventHasGame, discoveredHasGame]);

  useEffect(() => {
    if (event?.gameStateEmoji) setGameStateEmoji(event.gameStateEmoji);
    else if (!eventHasGame || event?.gameType !== 'emoji-fi') setGameStateEmoji(null);
  }, [event?.gameStateEmoji, eventHasGame, event?.gameType]);

  // Emoji game: at start of each new round, clear "Vastaus vastaanotettu" notifications
  useEffect(() => {
    if (!isEmojiGame || !gameStateEmoji?.currentRound) return;
    const roundKey = gameStateEmoji.currentRound.roundStartedAt || gameStateEmoji.currentRound.questionId;
    if (!roundKey || emojiRoundKeyClearedRef.current === roundKey) return;
    emojiRoundKeyClearedRef.current = roundKey;
    setGameDisplayMessages((prev) => prev.filter((m) => !/Vastaus vastaanotettu/i.test(m.text)));
  }, [isEmojiGame, gameStateEmoji]);

  // Prune game messages that have passed their removeAt time (so they disappear over time and don't stack)
  useEffect(() => {
    const t = setInterval(() => {
      setGameDisplayMessages((prev) => {
        const now = Date.now();
        const next = prev.filter((m) => !m.removeAt || now < m.removeAt);
        return next.length === prev.length ? prev : next;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Mark game data ready when we have emoji round from GET event (server has read emojis.json)
  useEffect(() => {
    if (isEmojiGame && event?.gameStateEmoji != null) {
      setGameDataReady(true);
    }
  }, [isEmojiGame, event?.gameStateEmoji]);

  // When event has started and has a game, load first question into game container on start; clear when no game or not started
  useEffect(() => {
    if ((!eventHasGame && !discoveredHasGame) || !eventStarted) {
      setGameDisplayPrompt(null);
      setGameQuestionStartedAt(null);
      setGameOptions(null);
      setAbcdRoundStartedAt(null);
      setGameDisplayMessages([]);
      if (!eventStarted) setGameDataReady(false);
      return;
    }
    if (isEmojiGame) {
      const round = event?.gameStateEmoji?.currentRound;
      if (round) setGameDisplayPrompt(`${round.emojis || ''}\n${round.question || ''}`);
      setGameQuestionStartedAt(null);
      return;
    }
    if (!eventId) return;
    fetch(`${API}/api/events/${encodeURIComponent(eventId)}/next-question`)
      .then((res) => (res.ok ? res.json() : { prompt: null, noGame: true }))
      .then((data) => {
        setGameDataReady(true);
        if (data?.prompt) setGameDisplayPrompt(data.prompt);
        else setGameDisplayPrompt(null);
        if (data?.options) setGameOptions(data.options);
        else setGameOptions(null);
        if (event?.gameType === 'abcd-fi') {
          // ABCD: start countdown only on first answer
          setGameQuestionStartedAt(null);
          setAbcdRoundStartedAt(null);
        } else if (data?.questionStartedAt != null) {
          setGameQuestionStartedAt(data.questionStartedAt);
        } else {
          setGameQuestionStartedAt(null);
        }
      })
      .catch(() => {});
  }, [
    event?.id,
    eventHasGame,
    discoveredHasGame,
    event?.gameStateEmoji,
    eventId,
    eventStarted,
    isEmojiGame,
    event?.gameType,
  ]);

  // Per-question timer: riddle/trivia 60s, ABCD 10s. When time runs out, call timeout-skip and show next question.
  useEffect(() => {
    if (isEmojiGame || !eventId || !gameDisplayPrompt || gameQuestionStartedAt == null) {
      if (questionTimerRef.current) {
        clearTimeout(questionTimerRef.current);
        questionTimerRef.current = null;
      }
      return;
    }
    const ROUND_DURATION_MS = event?.gameType === 'abcd-fi' ? 10 * 1000 : 60 * 1000;
    const delayMs = ROUND_DURATION_MS - (Date.now() - gameQuestionStartedAt);
    const runTimeout = () => {
      questionTimerRef.current = null;
      fetch(`${API}/api/events/${encodeURIComponent(eventId)}/game-skip?timeout=1`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.id }),
      })
        .then((res) => (res.ok ? res.json() : {}))
        .then((data) => {
          if (data?.nextQuestionPrompt != null) {
            setGameDisplayPrompt(data.nextQuestionPrompt);
          } else {
            setGameDisplayPrompt(null);
          }
          if (data?.options) setGameOptions(data.options);
          else setGameOptions(null);
          if (event?.gameType === 'abcd-fi') {
            // New ABCD round: reset countdown; first answer will start it again
            setGameQuestionStartedAt(null);
            setAbcdRoundStartedAt(null);
            setAbcdLockedOption(null);
            // Refresh event so TOP1 + personal score reflect newly awarded points
            if (eventId) {
              fetch(`${API}/api/events/${encodeURIComponent(eventId)}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((ev) => {
                  if (ev) setEvent(ev);
                })
                .catch(() => {});
            }
          } else if (data?.questionStartedAt != null) {
            setGameQuestionStartedAt(data.questionStartedAt);
          } else {
            setGameQuestionStartedAt(null);
          }
        })
        .catch(() => {});
    };
    if (delayMs <= 0) {
      runTimeout();
      return;
    }
    questionTimerRef.current = setTimeout(runTimeout, delayMs);
    return () => {
      if (questionTimerRef.current) {
        clearTimeout(questionTimerRef.current);
        questionTimerRef.current = null;
      }
    };
  }, [eventId, isEmojiGame, gameDisplayPrompt, gameQuestionStartedAt, user?.id, event?.gameType]);

  // When timer reaches 10 minutes, send a review DM to the attendee (once), only for keskustelu events.
  useEffect(() => {
    if (!user || !eventId || !event) return;
    const isKeskustelu = event.eventType === 'keskustelu' || event.eventType === 'stream' || event.eventType === 'livestream';
    if (!isKeskustelu) {
      if (!hasSentTenMinuteDm) setHasSentTenMinuteDm(true);
      return;
    }
    if (joinStartTime == null || hasSentTenMinuteDm) return;
    const creatorIdStr = event?.createdByUserId != null ? String(event.createdByUserId).replace(/n$/, '') : '';
    if (creatorIdStr === '1') {
      setHasSentTenMinuteDm(true); // suppress retries on every `now` tick
      return;
    }
    if (event?.createdByUserId != null && String(event.createdByUserId) === String(user.id)) return;
    const elapsedMs = now - joinStartTime;
    if (elapsedMs < 10 * 60 * 1000) return;

    const attendeeId = String(user.id).replace(/n$/, '');
    if (!attendeeId) return;

    fetch(`${API}/api/events/${encodeURIComponent(event.id)}/participation-dm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: attendeeId,
      }),
    }).catch(() => {});

    setHasSentTenMinuteDm(true);
  }, [now, user, eventId, event, joinStartTime, hasSentTenMinuteDm]);

  // Load event data once on mount.
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${API}/api/events/${eventId}?requesterUserId=${encodeURIComponent(user?.id || '')}`)
      .then((res) => {
        if (!res.ok) {
          if (!cancelled) {
            setEvent(null);
            setLoading(false);
          }
          return;
        }
        return res.json();
      })
      .then((data) => {
        if (cancelled || data === undefined) return;
        setEvent(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) {
          setEvent(null);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [eventId, user?.id]);

  // Refresh livestream window flags while on the page.
  useEffect(() => {
    if (!eventId || event?.eventType !== 'livestream') return undefined;
    const refresh = () => {
      fetch(`${API}/api/events/${eventId}?requesterUserId=${encodeURIComponent(user?.id || '')}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && !data.error) setEvent(data);
        })
        .catch(() => {});
    };
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [eventId, event?.eventType, user?.id]);

  const fetchChat = useCallback(() => {
    if (!eventId || !user) return;
    const username = user?.displayName || user?.username || '';
    const url = username 
      ? `${API}/api/chat/event/${eventId}?username=${encodeURIComponent(username)}` 
      : `${API}/api/chat/event/${eventId}`;
    fetch(url)
      .then((res) => parseJsonResponse(res))
      .then((data) => setChatMessages(normalizeChatMessages(data.messages || [], user?.id)))
      .catch((err) => {
        console.error(err);
        setChatMessages([]);
      });
  }, [eventId, user]);

  useEffect(() => {
    const isKeskusteluOnly = event?.eventType === 'keskustelu' || event?.eventType === 'stream';
    const hasStarted = startsAt > 0 && now >= startsAt;
    const hasGame = event?.eventType === 'game' && !!(event?.hasGame || event?.gameType);
    const shouldPollEmbeddedKeskusteluChat =
      (isKeskusteluOnly && hasStarted && !hasGame) ||
      (event?.eventType === 'livestream' && isOwnEvent && authorMainView === 'chat' && hasStarted && !hasGame);
    if (!user || !eventId || (!chatExpanded && !shouldPollEmbeddedKeskusteluChat)) return;
    fetchChat();
    const t = setInterval(fetchChat, 2000);
    return () => clearInterval(t);
  }, [user, eventId, chatExpanded, fetchChat, event, startsAt, now, isOwnEvent, authorMainView]);

  const kuiskaaCandidates = useMemo(() => buildKuiskaaCandidates(), []);

  // Emoji game: refetch event periodically to update participants (who has answered)
  useEffect(() => {
    if (!eventId || !isEmojiGame || !eventStarted) return;
    const t = setInterval(() => {
      fetch(`${API}/api/events/${eventId}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((ev) => {
          if (ev?.gameStateEmoji) setGameStateEmoji(ev.gameStateEmoji);
        })
        .catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [eventId, isEmojiGame, eventStarted]);

  // Emoji game: end round only when 60s timer has run out (client checks; server enforces 60s)
  useEffect(() => {
    if (!eventId || !isEmojiGame || !gameStateEmoji?.currentRound) return;
    const round = gameStateEmoji.currentRound;
    const roundKey = round.roundStartedAt || round.questionId;
    if (!roundKey) return;
    if (emojiRoundEndedRef.current === roundKey) return;
    const startMs = round.roundStartedAt ? new Date(round.roundStartedAt).getTime() : 0;
    const ROUND_DURATION_MS = 60 * 1000;
    const elapsed = startMs > 0 ? Date.now() - startMs : 0;
    const timerRanOut = startMs > 0 && elapsed >= ROUND_DURATION_MS;
    if (timerRanOut) {
      emojiRoundEndedRef.current = roundKey;
      fetch(`${API}/api/events/${encodeURIComponent(eventId)}/game-round-end`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.id }),
      })
        .then((res) => res.ok ? res.json() : null)
        .then((data) => {
          if (data?.roundNotEndedYet && data?.gameStateEmoji) {
            setGameStateEmoji(data.gameStateEmoji);
            emojiRoundEndedRef.current = null;
            return;
          }
          if (data?.gameStateEmoji) {
            setGameStateEmoji(data.gameStateEmoji);
            if (data.gameStateEmoji.currentRound) {
              emojiRoundEndedRef.current = null;
              setGameDisplayPrompt(
                `${data.gameStateEmoji.currentRound.emojis || ''}\n${data.gameStateEmoji.currentRound.question || ''}`,
              );
              setGameDisplayMessages((prev) => prev.filter((m) => !/Vastaus vastaanotettu/i.test(m.text)));
            }
          }
          fetch(`${API}/api/events/${eventId}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((ev) => { if (ev) setEvent(ev); })
            .catch(() => {});
        })
        .catch(() => {});
    }
  }, [eventId, isEmojiGame, gameStateEmoji, now, user?.id]);

  const handleGameAnswerResponse = useCallback(
    (data) => {
    const text = data?.result || 'Vastausta ei voitu tarkistaa.';
    // ABCD game: do not show "Vastaus vastaanotettu." toast, rely on locked option highlight instead
    const isAbcdGameType = data?.gameType === 'abcd-fi' || event?.gameType === 'abcd-fi';
    const shouldSuppressAck = isAbcdGameType && text === 'Vastaus vastaanotettu.';
    // Never show "game not yet started" when client considers event ongoing (e.g. server time skew in production)
    if (eventStarted && text === 'Peli ei ole vielä käynnissä.') {
      if (data?.gameStateEmoji) setGameStateEmoji(data.gameStateEmoji);
      if (data?.nextQuestionPrompt != null) {
        setGameDisplayPrompt(data.nextQuestionPrompt);
        if (data?.options) setGameOptions(data.options);
        else setGameOptions(null);
        if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
        else setGameQuestionStartedAt(null);
        setAbcdLockedOption(null);
      } else if (event?.gameType !== 'emoji-fi' && event?.gameType !== 'abcd-fi') {
        setGameDisplayPrompt(null);
        setGameQuestionStartedAt(null);
        setGameOptions(null);
        setAbcdLockedOption(null);
      }
      if (eventId) {
        fetch(`${API}/api/events/${encodeURIComponent(eventId)}`)
          .then((res) => (res.ok ? res.json() : null))
          .then((ev) => {
            if (ev) setEvent(ev);
            if (ev?.gameStateEmoji) setGameStateEmoji(ev.gameStateEmoji);
          })
          .catch(() => {});
      }
      return;
    }
    const msg = createGameMessage(text);
    const isEventStatusMsg =
      /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(text);
    if (data?.gameStateEmoji) setGameStateEmoji(data.gameStateEmoji);
    if (!shouldSuppressAck) {
      setGameDisplayMessages((prev) => {
        let filtered = prev;
        if (isEventStatusMsg) {
          filtered = filtered.filter(
            (m) =>
              !/Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(
                m.text,
              ),
          );
        }
        if (msg.removeAt) {
          filtered = filtered.filter(
            (m) => !/^Vastaus ei ole /i.test(m.text) && !/Väärin!?/.test(m.text),
          );
        }
        return [...filtered, msg];
      });
      if (msg.removeAt) {
        const id = msg.id;
        const removeDelay = /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(
          msg.text,
        )
          ? 3000
          : /Väärin!?|Vastaus vastaanotettu/.test(msg.text)
            ? 1000
            : 2500;
        setTimeout(() => {
          setGameDisplayMessages((prev) => prev.filter((m) => m.id !== id));
        }, removeDelay);
      }
    }
    if (data?.nextQuestionPrompt != null) {
      setGameDisplayPrompt(data.nextQuestionPrompt);
      if (data?.options) setGameOptions(data.options);
      else setGameOptions(null);
      if (event?.gameType === 'abcd-fi') {
        // For ABCD, next round's countdown starts on first answer
        setGameQuestionStartedAt(null);
        setAbcdRoundStartedAt(null);
      } else if (data?.questionStartedAt != null) {
        setGameQuestionStartedAt(data.questionStartedAt);
      } else {
        setGameQuestionStartedAt(null);
      }
      setAbcdLockedOption(null);
    } else if (event?.gameType !== 'emoji-fi' && event?.gameType !== 'abcd-fi') {
      setGameDisplayPrompt(null);
      setGameQuestionStartedAt(null);
      setGameOptions(null);
      setAbcdLockedOption(null);
    }
    if (eventId) {
      fetch(`${API}/api/events/${encodeURIComponent(eventId)}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((ev) => {
          if (ev) setEvent(ev);
          if (ev?.gameStateEmoji) setGameStateEmoji(ev.gameStateEmoji);
        })
        .catch(() => {});
    }
  },
  [eventStarted, event?.gameType, eventId]);

  useEffect(() => {
    setGameVastaaPanelOpen(false);
  }, [gameDisplayPrompt, gameStateEmoji?.currentRound?.roundStartedAt, gameStateEmoji?.currentRound?.questionId]);

  const submitGameAnswer = useCallback(
    (answer) => {
      const trimmed = (answer || '').trim();
      if (!trimmed) {
        setGameDisplayMessages((prev) => [
          ...prev,
          createGameMessage('/vastaa tai /arvaa sinun vastauksesi'),
        ]);
        return Promise.resolve(null);
      }
      if (!user || !eventId) return Promise.resolve(null);
      if (event && now >= getEventEndTime(event).getTime()) {
        setGameDisplayMessages((prev) => [...prev, createGameMessage('Tapahtuma on päättynyt')]);
        return Promise.resolve(null);
      }
      if (event?.gameType === 'abcd-fi') {
        const upper = trimmed.toUpperCase();
        if (upper[0] === 'A' || upper[0] === 'B' || upper[0] === 'C' || upper[0] === 'D') {
          if (!abcdRoundStartedAt) {
            const nowTs = Date.now();
            setAbcdRoundStartedAt(nowTs);
            setGameQuestionStartedAt(nowTs);
          }
          setAbcdLockedOption(upper[0]);
        }
      }
      return fetch(`${API}/api/events/${encodeURIComponent(eventId)}/game-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          displayName: user.displayName || user.username,
          answer: trimmed,
        }),
      })
        .then((res) => parseJsonResponse(res))
        .then((data) => {
          handleGameAnswerResponse(data);
          return data;
        })
        .catch(() => {
          setGameDisplayMessages((prev) => [...prev, createGameMessage('Vastausta ei voitu tarkistaa.')]);
          return null;
        });
    },
    [abcdRoundStartedAt, event, eventId, handleGameAnswerResponse, now, user],
  );

  const handleInlineVastaaSubmit = useCallback(
    (answer) => {
      setGameVastaaSubmitting(true);
      submitGameAnswer(answer)
        .then((data) => {
          if (shouldCloseGameAnswerPanel(data)) setGameVastaaPanelOpen(false);
        })
        .finally(() => setGameVastaaSubmitting(false));
    },
    [submitGameAnswer],
  );

  const reloadKysyQuestion = useCallback(() => {
    if (!eventId || !user) return;
    fetch(`${API}/api/events/${encodeURIComponent(eventId)}/next-question?resend=1`)
      .then((res) => (res.ok ? res.json() : { prompt: null, noGame: true }))
      .then((data) => {
        if (data?.prompt) {
          setGameDisplayPrompt(data.prompt);
          if (data?.options) setGameOptions(data.options);
          else setGameOptions(null);
          if (event?.gameType !== 'abcd-fi') {
            if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
            else setGameQuestionStartedAt(null);
          }
          setAbcdLockedOption(null);
        } else {
          setGameDisplayMessages((prev) => [
            ...prev,
            createGameMessage('Kysymystä ei saatu. Kokeile /kysy uudelleen.'),
          ]);
        }
      })
      .catch(() => {
        setGameDisplayMessages((prev) => [...prev, createGameMessage('Kysymystä ei saatu. Kokeile /kysy uudelleen.')]);
      });
  }, [eventId, user, event?.gameType]);

  const handleSendChat = useCallback((e) => {
    e.preventDefault();
    const raw = (chatInput || '').trim();
    if (!raw || !user || !eventId) return;
    // Handle resend / show question: /kysy — repeat current question in game container (resend=1 = do not pick new)
    if (/^\/kysy\s*$/i.test(raw)) {
      setChatInput('');
      setReplyingToMessage(null);
      reloadKysyQuestion();
      return;
    }
    // Handle host-only skip riddle: /ohi — reveal answer and go to next (owner only)
    if (/^\/ohi\s*$/i.test(raw)) {
      setChatInput('');
      setReplyingToMessage(null);
      fetch(`${API}/api/events/${encodeURIComponent(eventId)}/game-skip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      })
        .then((res) => res.json())
        .then((data) => {
          const text = data?.result || '—';
          if (eventStarted && text === 'Peli ei ole vielä käynnissä.') {
            if (data?.nextQuestionPrompt != null) {
              setGameDisplayPrompt(data.nextQuestionPrompt);
              if (data?.options) setGameOptions(data.options);
              else setGameOptions(null);
              if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
              else setGameQuestionStartedAt(null);
              setAbcdLockedOption(null);
            } else {
              setGameDisplayPrompt(null);
              setGameQuestionStartedAt(null);
              setGameOptions(null);
              setAbcdLockedOption(null);
            }
            return;
          }
          const msg = createGameMessage(text);
          const isEventStatusMsg = /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(text);
          setGameDisplayMessages((prev) => {
            let filtered = prev.filter((m) => !/Vastaus olisi ollut/.test(m.text));
            if (isEventStatusMsg) {
              filtered = filtered.filter((m) => !/Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(m.text));
            }
            return [...filtered, msg];
          });
          if (msg.removeAt) {
            const id = msg.id;
            const removeDelay = /Peli ei ole vielä käynnissä|Peli on päättynyt|Peli ei ole vielä alkanut|Tapahtuma on päättynyt/.test(msg.text) ? 3000 : 1000;
            setTimeout(() => {
              setGameDisplayMessages((prev) => prev.filter((m) => m.id !== id));
            }, removeDelay);
          }
          if (data?.nextQuestionPrompt != null) {
            setGameDisplayPrompt(data.nextQuestionPrompt);
            if (data?.options) setGameOptions(data.options);
            else setGameOptions(null);
            if (data?.questionStartedAt != null) setGameQuestionStartedAt(data.questionStartedAt);
            else setGameQuestionStartedAt(null);
            setAbcdLockedOption(null);
          } else {
            setGameDisplayPrompt(null);
            setGameQuestionStartedAt(null);
            setGameOptions(null);
            setAbcdLockedOption(null);
          }
        })
        .catch(() => {
          setGameDisplayMessages((prev) => [...prev, createGameMessage('Komento epäonnistui.')]);
        });
      return;
    }
    // Handle game answer: /vastaa or /arvaa — result and next question go to game container (skip for levyraati)
    const match = raw.match(/^\/(?:vastaa|arvaa)(?:\s+(.*))?$/i);
    if (match && event?.eventType === 'levyraati') {
      const vastaaText = (match[1] || '').trim();
      if (vastaaText && levyraatiPlaylist[levyraatiActiveIndex] && user?.id) {
        const track = levyraatiPlaylist[levyraatiActiveIndex];
        const key = getLevyraatiTrackKey(track);
        const existing = levyraatiFeedback[key];
        const nextFeedback = vastaaText.slice(0, 2000);
        setChatInput('');
        setReplyingToMessage(null);
        setLevyraatiFeedback((prev) => ({ ...prev, [key]: { ...prev[key], feedback: nextFeedback } }));
        fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-feedback${event?.isHidden ? '?includeHidden=1' : ''}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.id,
            track_author: track?.track_author ?? '',
            track_name: track?.track_name ?? '',
            rating: existing?.rating ?? null,
            feedback: nextFeedback,
          }),
        })
          .then((res) => (res.ok ? res.json() : null))
          .then(() => {
            const includeHidden = event?.isHidden ? '&includeHidden=1' : '';
            return fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-feedback?userId=${encodeURIComponent(user.id)}${includeHidden}`)
              .then((r) => (r.ok ? r.json() : null))
              .then((fb) => { if (fb != null && typeof fb === 'object') setLevyraatiFeedback((prev) => ({ ...prev, ...fb })); });
          })
          .catch(() => {});
        return;
      }
    }
    if (match && event?.eventType !== 'levyraati') {
      const answer = (match[1] || '').trim();
      setChatInput('');
      setReplyingToMessage(null);
      submitGameAnswer(answer);
      return;
    }
    const kuiskaaParsed = parseKuiskaaMessage(raw);
    if (kuiskaaParsed) {
      setChatInput('');
      setReplyingToMessage(null);
      setLastSentMessage(raw);
      const text = kuiskaaParsed.body.slice(0, 500);
      const userMsg = {
        text,
        authorName: user.displayName || user.username,
        authorId: user.id,
        eventId,
        whisperTo: kuiskaaParsed.target.slice(0, 100),
      };
      const optimisticMessage = {
        ...userMsg,
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        isOwn: true,
        createdAt: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, optimisticMessage]);
      fetch(`${API}/api/chat/event/${eventId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [userMsg] }),
      })
        .then((res) => parseJsonResponse(res))
        .then((data) => {
          setChatMessages(normalizeChatMessages(data.messages || [], user.id));
          setScrollTrigger((t) => t + 1);
        })
        .catch((err) => console.error(err));
      return;
    }
    setLastSentMessage(raw);
    const text = raw.slice(0, 500);
    const userMsg = {
      text,
      authorName: user.displayName || user.username,
      authorId: user.id,
      eventId,
      ...(replyingToMessage && {
        replyTo: {
          messageId: replyingToMessage.id,
          textPreview: shortenReplyPreview(replyingToMessage.text),
        },
      }),
    };
    // Optimistically show the user's message immediately
    const optimisticMessage = {
      ...userMsg,
      id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      isOwn: true,
      createdAt: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, optimisticMessage]);
    setChatInput('');
    setReplyingToMessage(null);
    fetch(`${API}/api/chat/event/${eventId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: [userMsg] }),
    })
      .then((res) => parseJsonResponse(res))
      .then((data) => {
        setChatMessages(normalizeChatMessages(data.messages || [], user.id));
        setScrollTrigger((t) => t + 1);
      })
      .catch((err) => console.error(err));
  }, [
    chatInput,
    replyingToMessage,
    user,
    eventId,
    event,
    eventStarted,
    submitGameAnswer,
    levyraatiPlaylist,
    levyraatiActiveIndex,
    levyraatiFeedback,
    reloadKysyQuestion,
  ]);

  const handleDismissSlashMessage = useCallback((id) => {
    setSlashResponses((prev) => prev.filter((m) => m.id !== id));
  }, []);

  const handleDeleteOwnMessage = useCallback((id) => {
    if (!window.confirm('Haluatko varmasti poistaa tämän viestin?')) return;
    if (editingMessageId === id) {
      setEditingMessageId(null);
      setEditingDraft('');
    }
    const next = chatMessages.filter((m) => m.id !== id);
    setChatMessages(normalizeChatMessages(next, user?.id));
    fetch(`${API}/api/chat/event/${eventId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: next }),
    }).catch(console.error);
  }, [chatMessages, editingMessageId, user?.id, eventId]);

  const handleStartEditOwnMessage = useCallback((msg) => {
    setEditingMessageId(msg.id);
    setEditingDraft(msg.text);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingMessageId == null) return;
    const text = editingDraft.trim().slice(0, 500);
    const updatedAt = new Date().toISOString();
    const next = chatMessages.map((m) =>
      m.id === editingMessageId ? { ...m, text: text || m.text, updatedAt } : m
    );
    setChatMessages(normalizeChatMessages(next, user?.id));
    setEditingMessageId(null);
    setEditingDraft('');
    fetch(`${API}/api/chat/event/${eventId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: next }),
    }).catch(console.error);
  }, [editingMessageId, editingDraft, chatMessages, user?.id, eventId]);

  const handleCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setEditingDraft('');
  }, []);

  const handleReplyToMessage = useCallback((msg) => {
    setReplyingToMessage({ id: msg.id, text: msg.text || '', authorName: msg.authorName });
    chatInputRef.current?.focus();
  }, []);

  const handleCancelReply = useCallback(() => {
    setReplyingToMessage(null);
  }, []);

  const handleEditInputKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEdit();
    } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSaveEdit();
    }
  }, [handleCancelEdit, handleSaveEdit]);

  const handleToggleMuteAuthor = useCallback((authorKey) => {
    if (!authorKey) return;
    setMutedAuthorKeys((prev) => {
      const next = prev.includes(authorKey) ? prev.filter((k) => k !== authorKey) : [...prev, authorKey];
      try {
        localStorage.setItem('chatMutedAuthors', JSON.stringify(next));
      } catch (e) {
        console.warn(e);
      }
      return next;
    });
  }, []);

  // Dockable chat drag handlers
  const handleMouseDown = useCallback((e) => {
    if (!chatDocked || !chatExpanded) return;
    // Only allow dragging from the header
    if (!e.target.closest('.EventLivestream-chat-header')) return;
    e.preventDefault();
    setIsDragging(true);
    const rect = chatWindowRef.current?.getBoundingClientRect();
    if (rect) {
      setDragStart({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  }, [chatDocked, chatExpanded]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => {
      if (chatDocked && chatWindowRef.current) {
        const newX = Math.max(0, Math.min(e.clientX - dragStart.x, window.innerWidth - chatWindowRef.current.offsetWidth));
        const newY = Math.max(0, Math.min(e.clientY - dragStart.y, window.innerHeight - chatWindowRef.current.offsetHeight));
        setChatPosition({ x: newX, y: newY });
      }
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, chatDocked, dragStart]);

  useEffect(() => {
    if (editingMessageId != null && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingMessageId]);

  useEffect(() => {
    if (user && chatInputRef.current && chatExpanded) {
      chatInputRef.current.focus();
    }
  }, [user, chatExpanded]);

  if (loading) {
    return (
      <div className="App-block">
        <p>Loading event...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="App-block">
        <p>Event not found.</p>
        <button type="button" className="Button" onClick={() => window.location.href = '/chat/'}>
          Takaisin
        </button>
      </div>
    );
  }

  const needsAreaUnlockCheck = !!areaUnlockProductId && !isOwnEvent;
  const showAreaLocked = needsAreaUnlockCheck && !areaUnlockLoading && !areaUnlockPurchased;
  const showAreaLoading = needsAreaUnlockCheck && areaUnlockLoading;

  return (
      <div className="App-block">
      <div className="EventLivestream-page">
        {/* Top Bar */}
        <div className="EventLivestream-topbar">
        <button
            type="button"
            className="Button EventLivestream-back"
            onClick={() => {
              setJoinStartTime(null);
              setHasSentTenMinuteDm(false);
              if (ratingTimerRef.current) {
                clearTimeout(ratingTimerRef.current);
                ratingTimerRef.current = null;
              }
              if (event?.id) {
                const payload = user?.id ? { action: 'logout', userId: user.id } : { action: 'logout' };
                fetch(`${API}/api/events/${encodeURIComponent(event.id)}/logs`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(payload),
                }).catch(() => {});
              }
              window.location.href = '/chat/';
            }}
          >
            ←
          </button>
          {eventHasGame && showPersonalScore && (
            <div className="EventLivestream-topbar-lead">
              <span className="EventLivestream-topbar-lead-label EventLivestream-topbar-lead-label--mobile-hide">Sinä</span>
              <span className="EventLivestream-topbar-lead-score">
                {personalScore} pts
              </span>
            </div>
          )}
          {isOwnEvent && isLivestreamEvent && (
            <button
              type="button"
              className="Button EventLivestream-view-toggle"
              onClick={() => {
                setAuthorMainView((v) => (v === 'stream' ? 'chat' : 'stream'));
                setChatExpanded(false);
              }}
            >
              {authorMainView === 'stream' ? 'Näytä chat' : 'Näytä striimi'}
            </button>
          )}
          <div className="EventLivestream-topbar-info">
            <h2 className="EventLivestream-title">{event.title || 'Event'}</h2>
            {event.createdByUserId && user?.id != null && String(event.createdByUserId) === String(user.id) ? (
              <span className="EventLivestream-author">sinun tapahtumasi</span>
            ) : (
              <span className="EventLivestream-author">By {event.author || '—'}</span>
            )}
          </div>
          {eventHasGame && (event.gameHighScoreDisplayName != null || event.gameHighScoreValue != null) && (
            <div className="EventLivestream-topbar-lead EventLivestream-topbar-lead--top1">
              <span className="EventLivestream-topbar-lead-label">TOP 1</span>
              <span className="EventLivestream-topbar-lead-name">
                {event.gameHighScoreDisplayName || '—'}
              </span>
              {event.gameHighScoreValue != null && (
                <span className="EventLivestream-topbar-lead-score">
                  {event.gameHighScoreValue} pts
                </span>
              )}
            </div>
          )}
        </div>

        {/* Main area: if event is a game, show game container; if event is a stream, show stream/idle container */}
        <div className="EventLivestream-container">
          {!eventStarted ? (
            <div className="EventLivestream-stream-container">
              <EventLivestreamIdle
                event={event}
                isOwnEvent={isOwnEvent}
                now={now}
              />
            </div>
          ) : showGameContainer ? (
            <div className="EventLivestream-game-container">
              {showAreaLoading ? (
                <p className="EventLivestream-area-locked EventLivestream-area-locked--loading">Tarkistetaan…</p>
              ) : showAreaLocked ? (
                <EventAreaLockedNotice />
              ) : (
                <>
              <div className="EventLivestream-game-play-section">
                <div className="EventLivestream-game-inline-player">
                  <ChatInlinePlayer />
                </div>
                <div className="EventLivestream-game-play-content">
                  {isEmojiGame && (() => {
                    const round = gameStateEmoji?.currentRound;
                    const startMs = round?.roundStartedAt ? new Date(round.roundStartedAt).getTime() : 0;
                    const secondsLeft = startMs ? Math.max(0, Math.ceil((startMs + 60000 - now) / 1000)) : 60;
                    const emojiRowClickable = isEmojiGame && !eventHasEnded;
                    return (
                      round && (
                        <>
                          <div
                            className={
                              emojiGameEmojiRowClass +
                              (emojiRowClickable ? ' EventLivestream-game-clickable-answer' : '')
                            }
                            role={emojiRowClickable ? 'button' : undefined}
                            tabIndex={emojiRowClickable ? 0 : undefined}
                            onClick={emojiRowClickable ? () => setGameVastaaPanelOpen(true) : undefined}
                            onKeyDown={
                              emojiRowClickable
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setGameVastaaPanelOpen(true);
                                    }
                                  }
                                : undefined
                            }
                          >
                            {round.emojis}
                          </div>
                          <div className="EventLivestream-game-emoji-question">{round.question}</div>
                          <div className="EventLivestream-game-emoji-timer">
                            Aikaa: {secondsLeft} s
                          </div>
                          <GameAnswerInlinePanel
                            open={gameVastaaPanelOpen}
                            onClose={() => setGameVastaaPanelOpen(false)}
                            onSubmitAnswer={handleInlineVastaaSubmit}
                            submitting={gameVastaaSubmitting}
                          />
                        </>
                      )
                    );
                  })()}
                  {isAbcdGame && (
                    <>
                      {abcdRoundStartedAt && (
                        <div className="EventLivestream-abcd-timer">
                          Uusi kierros alkaa{' '}
                          {Math.max(
                            0,
                            Math.ceil(
                              (abcdRoundStartedAt + 10000 - now) / 1000,
                            ),
                          )}{' '}
                          s
                        </div>
                      )}
                      {gameDisplayPrompt && (
                        <div className="EventLivestream-game-prompt">{gameDisplayPrompt}</div>
                      )}
                      {gameOptions && (
                        <div className="EventLivestream-abcd-options">
                          {['A', 'B', 'C', 'D'].map((key) =>
                            gameOptions[key] ? (
                              <div
                                key={key}
                                className={
                                  'EventLivestream-abcd-option' +
                                  (abcdLockedOption === key
                                    ? ' EventLivestream-abcd-option--selected'
                                    : '')
                                }
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  // Always highlight locally
                                  setAbcdLockedOption(key);
                                  if (!user || !eventId) return;
                                  if (!abcdRoundStartedAt) {
                                    const nowTs = Date.now();
                                    setAbcdRoundStartedAt(nowTs);
                                    setGameQuestionStartedAt(nowTs);
                                  }
                                  fetch(
                                    `${API}/api/events/${encodeURIComponent(eventId)}/game-answer`,
                                    {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        userId: user.id,
                                        displayName: user.displayName || user.username,
                                        answer: key,
                                      }),
                                    },
                                  )
                                    .then((res) => parseJsonResponse(res))
                                    .then((data) => {
                                      handleGameAnswerResponse(data);
                                    })
                                    .catch(() => {
                                      setGameDisplayMessages((prev) => [
                                        ...prev,
                                        createGameMessage('Vastausta ei voitu tarkistaa.'),
                                      ]);
                                    });
                                }}
                              >
                                <span className="EventLivestream-abcd-option-key">{key}</span>
                                <span className="EventLivestream-abcd-option-text">
                                  {gameOptions[key]}
                                </span>
                              </div>
                            ) : null
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {!isEmojiGame && !isAbcdGame && gameDisplayPrompt && (
                    isRiddleOrTriviaGame ? (
                      <>
                        <div
                          className={
                            'EventLivestream-game-prompt' +
                            (!eventHasEnded ? ' EventLivestream-game-clickable-answer' : '')
                          }
                          role={!eventHasEnded ? 'button' : undefined}
                          tabIndex={!eventHasEnded ? 0 : undefined}
                          onClick={!eventHasEnded ? () => setGameVastaaPanelOpen(true) : undefined}
                          onKeyDown={
                            !eventHasEnded
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setGameVastaaPanelOpen(true);
                                  }
                                }
                              : undefined
                          }
                        >
                          {gameDisplayPrompt}
                        </div>
                        <GameAnswerInlinePanel
                          open={gameVastaaPanelOpen}
                          onClose={() => setGameVastaaPanelOpen(false)}
                          onSubmitAnswer={handleInlineVastaaSubmit}
                          submitting={gameVastaaSubmitting}
                        />
                      </>
                    ) : (
                      <div className="EventLivestream-game-prompt">{gameDisplayPrompt}</div>
                    )
                  )}
                  {isEmojiGame && gameDisplayPrompt && !gameStateEmoji?.currentRound && (() => {
                    const raw = String(gameDisplayPrompt);
                    const nl = raw.indexOf('\n');
                    const emojiLine = (nl === -1 ? raw : raw.slice(0, nl)).trim();
                    const questionRest = nl === -1 ? '' : raw.slice(nl + 1).trim();
                    const emojiRowClickable = isEmojiGame && !eventHasEnded;
                    return (
                      <>
                        {emojiLine ? (
                          <div
                            className={
                              emojiGameEmojiRowClass +
                              (emojiRowClickable ? ' EventLivestream-game-clickable-answer' : '')
                            }
                            role={emojiRowClickable ? 'button' : undefined}
                            tabIndex={emojiRowClickable ? 0 : undefined}
                            onClick={emojiRowClickable ? () => setGameVastaaPanelOpen(true) : undefined}
                            onKeyDown={
                              emojiRowClickable
                                ? (e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setGameVastaaPanelOpen(true);
                                    }
                                  }
                                : undefined
                            }
                          >
                            {emojiLine}
                          </div>
                        ) : null}
                        {questionRest ? (
                          <div className="EventLivestream-game-emoji-question">{questionRest}</div>
                        ) : null}
                        <GameAnswerInlinePanel
                          open={gameVastaaPanelOpen}
                          onClose={() => setGameVastaaPanelOpen(false)}
                          onSubmitAnswer={handleInlineVastaaSubmit}
                          submitting={gameVastaaSubmitting}
                        />
                      </>
                    );
                  })()}
                  {!gameDisplayPrompt && !(isEmojiGame && gameStateEmoji?.currentRound) && (
                    <div
                      className={
                        'EventLivestream-game-fetch-prompt' +
                        (user && eventId ? ' EventLivestream-game-fetch-prompt--clickable' : '')
                      }
                      role={user && eventId ? 'button' : undefined}
                      tabIndex={user && eventId ? 0 : undefined}
                      onClick={user && eventId ? () => reloadKysyQuestion() : undefined}
                      onKeyDown={
                        user && eventId
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                reloadKysyQuestion();
                              }
                            }
                          : undefined
                      }
                      aria-label={user && eventId ? 'Näytä kysymys' : undefined}
                      title={user && eventId ? 'Näytä kysymys' : undefined}
                    >
                      <p>
                        Kirjoita chatissa <code>/kysy</code> nähdäksesi kysymyksen.
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="EventLivestream-game-messages">
                {gameDisplayMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`EventLivestream-game-message${msg.removeAt ? ' EventLivestream-game-message--fade' : ''}${msg.shortFade ? ' EventLivestream-game-message--fade-1s' : ''}`}
                  >
                    {msg.text}
                  </div>
                ))}
              </div>
              <div className="EventLivestream-game-commands" aria-label="Komennot">
                {eventEndsInHint ? (
                  <p className="EventLivestream-game-hint">{eventEndsInHint}</p>
                ) : null}
                <span className="EventLivestream-game-commands-title">Komennot:</span>
                <ul className="EventLivestream-game-commands-list">

                      <li><code>/vastaa [vastaus]</code> Lähetä vastaus</li>
                      <li><code>/kysy</code> — kysy uudelleen</li>
                      {isOwnEvent && (
                        <li><code>/ohi</code> — vain järjestäjä: paljasta vastaus ja siirry seuraavaan</li>
                      )}

                </ul>
              </div>
                </>
              )}
            </div>
          ) : showLevyraatiContainer && !levyraatiEnded ? (
            <div className={`levyraati-container ${levyraatiFadeOut ? 'levyraati-container--fade-out' : ''}`}>
              {showAreaLoading ? (
                <p className="EventLivestream-area-locked EventLivestream-area-locked--loading">Tarkistetaan…</p>
              ) : showAreaLocked ? (
                <EventAreaLockedNotice />
              ) : (
                <>
              {levyraatiThankYouVisible && (
                <div className="levyraati-thankyou-overlay" aria-live="polite">
                  <span className="levyraati-thankyou-text">Kiitos!</span>
                </div>
              )}
              {levyraatiPlaylist.length === 0 ? (
                <p className="levyraati-empty">Ei kappaleita listalla.</p>
              ) : (
                <>
                  {/* Single player bar: play/stop, mute, seek, Next (only when current track rated) */}
                  <div className="levyraati-player-bar">
                    <audio
                      className="levyraati-audio-el"
                      ref={levyraatiAudioRef}
                      src={levyraatiStreamUrl || undefined}
                      muted={levyraatiMuted}
                      onTimeUpdate={() => {
                        const el = levyraatiAudioRef.current;
                        if (el) setLevyraatiCurrentTime(el.currentTime);
                      }}
                      onDurationChange={() => {
                        const el = levyraatiAudioRef.current;
                        if (el) setLevyraatiDuration(el.duration);
                      }}
                      onPlay={() => setLevyraatiPlaying(true)}
                      onPause={() => setLevyraatiPlaying(false)}
                      onEnded={() => setLevyraatiPlaying(false)}
                    />
                    {levyraatiPlaylist[levyraatiActiveIndex] && (
                      <>
                        <div className="levyraati-player-bar-info">
                          <strong>{levyraatiPlaylist[levyraatiActiveIndex].track_author ?? '—'}</strong>
                          {' – '}
                          {levyraatiPlaylist[levyraatiActiveIndex].track_name ?? '—'}
                        </div>
                        <div className="levyraati-player-bar-controls">
                          <button
                            type="button"
                            className="Button levyraati-player-btn"
                            disabled={levyraatiFirstIncompleteIndex == null}
                            onClick={() => {
                              if (levyraatiFirstIncompleteIndex == null) return;
                              setLevyraatiActiveIndex(levyraatiFirstIncompleteIndex);
                              setLevyraatiCurrentTime(0);
                              setLevyraatiDuration(0);
                              const el = levyraatiAudioRef.current;
                              if (el) {
                                el.pause();
                                el.currentTime = 0;
                                setLevyraatiPlaying(false);
                              }
                            }}
                            aria-label="Hyppää ensimmäiseen keskeneräiseen kappaleeseen"
                            title="Hyppää ensimmäiseen keskeneräiseen kappaleeseen"
                          >
                            <RotateCcw size={18} />
                          </button>
                          <button
                            type="button"
                            className="Button levyraati-player-btn"
                            disabled={levyraatiStreamLoading || !levyraatiStreamUrl}
                            onClick={() => {
                              const el = levyraatiAudioRef.current;
                              if (!el) return;
                              if (levyraatiPlaying) {
                                el.pause();
                              } else {
                                el.play().catch(() => {});
                              }
                            }}
                            aria-label={levyraatiPlaying ? 'Pysäytä' : 'Toista'}
                            title={levyraatiPlaying ? 'Pysäytä' : 'Toista'}
                          >
                            {levyraatiStreamLoading ? '…' : levyraatiPlaying ? '■' : '▶'}
                          </button>
                          <button
                            type="button"
                            className="Button levyraati-player-btn"
                            disabled={!levyraatiStreamUrl}
                            onClick={() => setLevyraatiMuted((m) => !m)}
                            aria-label={levyraatiMuted ? 'Ääni päälle' : 'Mykistä'}
                            title={levyraatiMuted ? 'Ääni päälle' : 'Mykistä'}
                          >
                            {levyraatiMuted ? '🔇' : '🔊'}
                          </button>
                          <div className="levyraati-player-seek">
                            <input
                              type="range"
                              min={0}
                              max={levyraatiDuration || 100}
                              step={0.1}
                              value={levyraatiCurrentTime}
                              onChange={(e) => {
                                const t = Number(e.target.value);
                                const el = levyraatiAudioRef.current;
                                if (el) {
                                  el.currentTime = t;
                                  setLevyraatiCurrentTime(t);
                                }
                              }}
                              className="levyraati-seek-input"
                              aria-label="Siirrä toistoa"
                            />
                            <span className="levyraati-seek-time">
                              {Math.floor((levyraatiCurrentTime || 0) / 60)}:{String(Math.floor((levyraatiCurrentTime || 0) % 60)).padStart(2, '0')}
                              {' / '}
                              {Number.isFinite(levyraatiDuration) ? `${Math.floor(levyraatiDuration / 60)}:${String(Math.floor(levyraatiDuration % 60)).padStart(2, '0')}` : '0:00'}
                            </span>
                          </div>
                          {levyraatiPlayableCount > 0 && (
                            <div className="levyraati-player-progress">
                              <span className="levyraati-player-progress-line levyraati-player-progress-line--ratings">
                                Arvostelut: {levyraatiGivenRatingsCount} / {levyraatiPlayableCount}
                              </span>
                              <span className="levyraati-player-progress-line levyraati-player-progress-line--feedback">
                                Palaute: {levyraatiGivenFeedbackCount} / {levyraatiPlayableCount}
                              </span>
                            </div>
                          )}
                          {(() => {
                            const currentTrack = levyraatiPlaylist[levyraatiActiveIndex];
                            const key = getLevyraatiTrackKey(currentTrack);
                            const f = levyraatiFeedback[key];
                            const rated = f?.rating != null;
                            const hasFeedback = f?.feedback != null;
                            const reviewed = rated || hasFeedback;
                            const hasNext = levyraatiActiveIndex < levyraatiPlaylist.length - 1;
                            const endLevyraatiClick = () => {
                              if (!eventId || !user?.id) return;
                              fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-end`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ userId: user.id }),
                              })
                                .then((res) => {
                                  if (!res.ok) return;
                                  setLevyraatiThankYouVisible(true);
                                  const el = levyraatiAudioRef.current;
                                  if (el) {
                                    el.pause();
                                    setLevyraatiPlaying(false);
                                  }
                                  setTimeout(() => setLevyraatiFadeOut(true), 1500);
                                  setTimeout(() => setLevyraatiEnded(true), 2500);
                                })
                                .catch(() => {});
                            };
                            if (levyraatiAllRatedAndFeedbacked) {
                              return (
                                <button
                                  type="button"
                                  className="Button levyraati-player-btn levyraati-player-next"
                                  onClick={endLevyraatiClick}
                                  aria-label="Päätä levyraati"
                                  title="Päätä levyraati"
                                >
                                  Päätä levyraati
                                </button>
                              );
                            }
                            if (reviewed && hasNext) {
                              return (
                                <button
                                  type="button"
                                  className="Button levyraati-player-btn levyraati-player-next"
                                  onClick={() => {
                                    setLevyraatiActiveIndex((prev) => Math.min(prev + 1, levyraatiPlaylist.length - 1));
                                    setLevyraatiCurrentTime(0);
                                    setLevyraatiDuration(0);
                                    const el = levyraatiAudioRef.current;
                                    if (el) {
                                      el.pause();
                                      setLevyraatiPlaying(false);
                                    }
                                  }}
                                  aria-label="Seuraava kappale"
                                  title="Seuraava kappale"
                                >
                                  Seuraava ›
                                </button>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                  {/* Single track card: current track only (player above controls playback) */}
                  {levyraatiPlaylist[levyraatiActiveIndex] && (() => {
                    const track = levyraatiPlaylist[levyraatiActiveIndex];
                    const key = getLevyraatiTrackKey(track);
                    const myFeedback = levyraatiFeedback[key];
                    const trackUrl = track?.url ?? '';
                    return (
                      <div className="levyraati-track levyraati-track--now-playing">
                        <div className="levyraati-about-row">
                          <strong>{track?.track_author ?? '—'}</strong> – {track?.track_name ?? '—'}
                          {trackUrl ? <a href={trackUrl} target="_blank" rel="noopener noreferrer" className="levyraati-about-link">Linkki</a> : null}
                        </div>
                        <div className="levyraati-stars-row">
                          {myFeedback?.rating != null ? (
                            <>
                              <div className="levyraati-stars levyraati-stars--rated">
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <span
                                    key={n}
                                    className={`levyraati-star levyraati-star--filled ${n <= myFeedback.rating ? 'levyraati-star--active' : ''}`}
                                    aria-hidden
                                  >
                                    ★
                                  </span>
                                ))}
                              </div>
                              <span className="levyraati-rating-value">Arvostelusi {myFeedback.rating}/5</span>
                              <button
                                type="button"
                                className="Button Button-secondary levyraati-retract"
                                onClick={() => {
                                  if (!user?.id || !eventId) return;
                                  fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-feedback`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ userId: user.id, track_author: track?.track_author ?? '', track_name: track?.track_name ?? '', rating: null }),
                                  }).then(() => {
                                    setLevyraatiFeedback((prev) => {
                                      const next = { ...prev };
                                      if (next[key]) { next[key] = { ...next[key], rating: null }; if (!next[key].feedback) delete next[key]; }
                                      return next;
                                    });
                                  });
                                }}
                              >
                                Peru arvio
                              </button>
                            </>
                          ) : (
                            <>
                              <p className="levyraati-instruction">näpäytä tähtiä antaaksesi arvosanan tälle biisille (1–5)</p>
                              <div
                                className="levyraati-stars"
                                onMouseLeave={() => setLevyraatiStarHover(null)}
                              >
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <button
                                    key={n}
                                    type="button"
                                    className={`levyraati-star ${levyraatiStarHover != null && n <= levyraatiStarHover ? 'levyraati-star--active' : ''}`}
                                    onMouseEnter={() => setLevyraatiStarHover(n)}
                                    onClick={() => {
                                      if (!user?.id || !eventId) return;
                                      const includeHidden = event?.isHidden ? '?includeHidden=1' : '';
                                  fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-feedback`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({
                                          userId: user.id,
                                          track_author: track?.track_author ?? '',
                                          track_name: track?.track_name ?? '',
                                          rating: n,
                                          feedback: myFeedback?.feedback ?? null,
                                        }),
                                      }).then(() => {
                                        setLevyraatiFeedback((prev) => ({ ...prev, [key]: { ...prev[key], rating: n } }));
                                        fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-feedback?userId=${encodeURIComponent(user.id)}${includeHidden}`)
                                          .then((res) => (res.ok ? res.json() : null))
                                          .then((data) => {
                                            if (data != null && typeof data === 'object') {
                                              setLevyraatiFeedback((prev) => ({ ...prev, ...data }));
                                            }
                                          })
                                          .catch(() => {});
                                      });
                                    }}
                                    aria-label={`${n} tähteä`}
                                  >
                                    ★
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                        <div className="levyraati-instructions-row">
                          {myFeedback?.feedback != null && myFeedback.feedback !== '' ? (
                            <>
                              <p>Kiitos palautteestasi!</p>
                              <button
                                type="button"
                                className="Button Button-secondary levyraati-retract"
                                onClick={() => {
                                  if (!user?.id || !eventId) return;
                                  fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-feedback`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ userId: user.id, track_author: track?.track_author ?? '', track_name: track?.track_name ?? '', feedback: null }),
                                  }).then(() => {
                                    setLevyraatiFeedback((prev) => {
                                      const next = { ...prev };
                                      if (next[key]) { next[key] = { ...next[key], feedback: null }; if (next[key].rating == null && !next[key].feedback) delete next[key]; }
                                      return next;
                                    });
                                  });
                                }}
                              >
                                Peru palaute
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="Button levyraati-instruction"
                              onClick={() => {
                                if (!user?.id) return;
                                const k = getLevyraatiTrackKey(track);
                                setLevyraatiFeedbackModalTrack({
                                  track_author: track?.track_author ?? '',
                                  track_name: track?.track_name ?? '',
                                  key: k,
                                });
                                setLevyraatiFeedbackDraft(
                                  myFeedback?.feedback != null && myFeedback.feedback !== ''
                                    ? myFeedback.feedback
                                    : '',
                                );
                                setLevyraatiFeedbackModalError(null);
                                setLevyraatiFeedbackModalOpen(true);
                              }}
                            >
                              /vastaa antaaksesi palautetta
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  {levyraatiPlaylist.length > 0 && levyraatiAllRatedAndFeedbacked && (
                    <div className="levyraati-end-row">
                      <button
                        type="button"
                        className="Button levyraati-end-btn"
                        onClick={() => {
                          if (!eventId || !user?.id) return;
                          fetch(`${API}/api/events/${encodeURIComponent(eventId)}/levyraati-end`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ userId: user.id }),
                          })
                            .then((res) => {
                              if (!res.ok) return;
                              setLevyraatiThankYouVisible(true);
                              const el = levyraatiAudioRef.current;
                              if (el) {
                                el.pause();
                                setLevyraatiPlaying(false);
                              }
                              setTimeout(() => setLevyraatiFadeOut(true), 1500);
                              setTimeout(() => setLevyraatiEnded(true), 2500);
                            })
                            .catch(() => {});
                        }}
                        aria-label="Päätä levyraati"
                        title="Päätä levyraati"
                      >
                        Päätä levyraati
                      </button>
                    </div>
                  )}
                </>
              )}
                </>
              )}
            </div>
          ) : isMovieEvent && eventStarted ? (
            <div className="EventLivestream-stream-container Elokuva-container">
              <Player
                src={pulinaMoviesUrl('bigBuckBunny/big_buck_bunny_720p_surround.mp4')}
                poster={pulinaMoviesUrl('bigBuckBunny/BBB.jpg')}
                subtitles=""
                fullscreenOnlyControls
                showCoverPlayOverlay
              />
            </div>
          ) : isLivestreamEvent && eventStarted && !eventHasGame ? (
            <div className="EventLivestream-stream-container">
              {showEmbeddedKeskusteluChat ? (
                <EventLivestreamIdle event={event} isOwnEvent={isOwnEvent} now={now} showBall={false}>
                  <div className="EventLivestream-idle-chat">
                    <div className="EventLivestream-chat-messages">
                      <ChatMessages
                        messages={[...chatMessages, ...slashResponses]}
                        editingMessageId={editingMessageId}
                        editingDraft={editingDraft}
                        setEditingDraft={setEditingDraft}
                        editInputRef={editInputRef}
                        onStartEdit={handleStartEditOwnMessage}
                        onSaveEdit={handleSaveEdit}
                        onCancelEdit={handleCancelEdit}
                        onDeleteMessage={handleDeleteOwnMessage}
                        onReply={handleReplyToMessage}
                        onEditInputKeyDown={handleEditInputKeyDown}
                        currentUserDisplayName={user?.displayName || user?.username}
                        profileRedirectPath={`/event/stream/${eventId}`}
                        mutedAuthorKeys={mutedAuthorKeys}
                        onToggleMuteAuthor={handleToggleMuteAuthor}
                        onDismissMessage={handleDismissSlashMessage}
                        autoScroll={false}
                        scrollToEndTrigger={scrollTrigger}
                      />
                    </div>
                    <ChatInput
                      inputRef={chatInputRef}
                      value={chatInput}
                      onChangeValue={setChatInput}
                      onSubmit={handleSendChat}
                      lastSentMessage={lastSentMessage}
                      placeholder="Kirjoita viesti..."
                      maxLength={500}
                      replyingTo={replyingToMessage}
                      onCancelReply={handleCancelReply}
                      mentionCandidates={Array.from(
                        new Set([
                          'pulinabotti',
                          ...(chatMessages || []).map((m) => m.authorName).filter(Boolean),
                        ]),
                      )}
                      kuiskaaCandidates={kuiskaaCandidates}
                      currentUser={user}
                      leftSlot={<ChatInlinePlayer />}
                    />
                  </div>
                </EventLivestreamIdle>
              ) : showStream && streamSrc ? (
                <>
                  <HlsPlayer src={streamSrc} autoPlay controls liveEdgeOnJoin />
                  {event.livestreamPlaybackPageUrl ? (
                    <p className="EventLivestream-playback-url" aria-hidden="true">
                      {event.livestreamPlaybackPageUrl}
                    </p>
                  ) : null}
                </>
              ) : (
                <EventLivestreamIdle event={event} isOwnEvent={isOwnEvent} now={now}>
                  {isLivestreamEvent && !streamPlaybackActive ? (
                    <p className="EventLivestream-window-hint">
                      Striimi avautuu, kun tapahtuma alkaa.
                    </p>
                  ) : null}
                  {isOwnEvent && streamPublishActive && event.livestreamIngestUrl ? (
                    <div className="EventLivestream-ingest-url">
                      <p><strong>Lähetys-URL (OBS)</strong></p>
                      <code>{event.livestreamIngestUrl}</code>
                      <p className="EventLivestream-window-hint">
                        Julkaisu avoinna 10 minuuttia ennen alkua tapahtuman loppuun asti. OBS: Server = alla oleva URL, Stream key tyhjä.
                      </p>
                    </div>
                  ) : isOwnEvent && isLivestreamEvent && !streamPublishActive ? (
                    <p className="EventLivestream-window-hint">
                      Lähetys-URL aktivoituu 10 minuuttia ennen tapahtuman alkua.
                    </p>
                  ) : null}
                </EventLivestreamIdle>
              )}
            </div>
          ) : (isStreamEvent || isLevyraatiEvent || isMovieEvent) && !eventHasGame ? (
            <div className="EventLivestream-stream-container">
              {showEmbeddedKeskusteluChat ? (
                <EventLivestreamIdle
                  event={event}
                  isOwnEvent={isOwnEvent}
                  now={now}
                  showBall={false}
                >
                  <div className="EventLivestream-idle-chat">
                    <div className="EventLivestream-chat-messages">
                      <ChatMessages
                        messages={[...chatMessages, ...slashResponses]}
                        editingMessageId={editingMessageId}
                        editingDraft={editingDraft}
                        setEditingDraft={setEditingDraft}
                        editInputRef={editInputRef}
                        onStartEdit={handleStartEditOwnMessage}
                        onSaveEdit={handleSaveEdit}
                        onCancelEdit={handleCancelEdit}
                        onDeleteMessage={handleDeleteOwnMessage}
                        onReply={handleReplyToMessage}
                        onEditInputKeyDown={handleEditInputKeyDown}
                        currentUserDisplayName={user?.displayName || user?.username}
                        profileRedirectPath={`/event/stream/${eventId}`}
                        mutedAuthorKeys={mutedAuthorKeys}
                        onToggleMuteAuthor={handleToggleMuteAuthor}
                        onDismissMessage={handleDismissSlashMessage}
                        autoScroll={false}
                        scrollToEndTrigger={scrollTrigger}
                      />
                    </div>
                    <ChatInput
                      inputRef={chatInputRef}
                      value={chatInput}
                      onChangeValue={setChatInput}
                      onSubmit={handleSendChat}
                      lastSentMessage={lastSentMessage}
                      placeholder="Kirjoita viesti..."
                      maxLength={500}
                      replyingTo={replyingToMessage}
                      onCancelReply={handleCancelReply}
                      mentionCandidates={Array.from(
                        new Set([
                          'pulinabotti',
                          ...(chatMessages || []).map((m) => m.authorName).filter(Boolean),
                        ])
                      )}
                      kuiskaaCandidates={kuiskaaCandidates}
                      currentUser={user}
                      leftSlot={!isLevyraatiEvent ? <ChatInlinePlayer /> : null}
                    />
                  </div>
                </EventLivestreamIdle>
              ) : showStream && streamSrc ? (
                <iframe
                  className="EventLivestream-iframe"
                  src={streamSrc}
                  title={event.title || 'Event stream'}
                  allow="accelerometer; encrypted-media; gyroscope"
                  allowFullScreen
                />
              ) : (
                <EventLivestreamIdle
                  event={event}
                  isOwnEvent={isOwnEvent}
                  now={now}
                />
              )}
            </div>
          ) : null}
        </div>

        {levyraatiFeedbackModalOpen && levyraatiFeedbackModalTrack && (
          <div
            className="LevyraatiFeedbackModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="levyraati-feedback-modal-title"
          >
            <div className="LevyraatiFeedbackModal-card">
              <h2 id="levyraati-feedback-modal-title" className="LevyraatiFeedbackModal-title">
                Palaute kappaleesta
              </h2>
              <p className="LevyraatiFeedbackModal-track">
                <strong>{levyraatiFeedbackModalTrack.track_author || '—'}</strong>
                {' – '}
                {levyraatiFeedbackModalTrack.track_name || '—'}
              </p>
              <label className="LevyraatiFeedbackModal-label" htmlFor="levyraati-feedback-textarea">
                Kirjoita palautteesi
                <textarea
                  id="levyraati-feedback-textarea"
                  className="LevyraatiFeedbackModal-textarea"
                  value={levyraatiFeedbackDraft}
                  onChange={(e) => setLevyraatiFeedbackDraft(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  disabled={levyraatiFeedbackSubmitting}
                />
              </label>
              {levyraatiFeedbackModalError && (
                <p className="LevyraatiFeedbackModal-error">{levyraatiFeedbackModalError}</p>
              )}
              <div className="LevyraatiFeedbackModal-actions">
                <button
                  type="button"
                  className="Button Button-danger"
                  disabled={levyraatiFeedbackSubmitting}
                  onClick={closeLevyraatiFeedbackModal}
                >
                  Peruuta
                </button>
                <button
                  type="button"
                  className="Button"
                  disabled={levyraatiFeedbackSubmitting || !levyraatiFeedbackDraft.trim()}
                  onClick={submitLevyraatiFeedbackModal}
                >
                  {levyraatiFeedbackSubmitting ? 'Lähetetään…' : 'Lähetä'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showRatingPrompt && user && (
          <div className="EventRatingPrompt">
            <div className="EventRatingPrompt-card">
              <p>
                Kiitos kun osallistuit tapahtumaan{' '}
                <strong>{event.title || '—'}</strong>, jonka järjesti{' '}
                <strong>{event.author || '—'}</strong>.
              </p>
              <div className="EventRatingPrompt-stars">
                {[0, 1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    className={n === ratingValue ? 'Star Star--active' : 'Star'}
                    onClick={() => setRatingValue(n)}
                    aria-label={`${n} tähteä`}
                  >
                    {n}★
                  </button>
                ))}
              </div>
              <label className="EventRatingPrompt-label">
                Kerro halutessasi, miksi annoit tämän arvion (valinnainen)
                <textarea
                  value={ratingReason}
                  onChange={(e) => setRatingReason(e.target.value)}
                  rows={3}
                />
              </label>
              {ratingError && <p className="EventRatingPrompt-error">{ratingError}</p>}
              <div className="EventRatingPrompt-actions">
                <button
                  type="button"
                  className="Button Button-secondary"
                  onClick={() => {
                    setShowRatingPrompt(false);
                    if (ratingTimerRef.current) {
                      clearTimeout(ratingTimerRef.current);
                      ratingTimerRef.current = null;
                    }
                  }}
                >
                  Sulje
                </button>
                <button
                  type="button"
                  className="Button"
                  disabled={submittingRating}
                  onClick={async () => {
                    if (!event?.id || !user?.id) return;
                    setSubmittingRating(true);
                    setRatingError(null);
                    try {
                      await fetch(`${API}/api/events/${encodeURIComponent(event.id)}/ratings`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          rating: ratingValue,
                          reason: ratingReason,
                          userId: user.id,
                        }),
                      });
                      // Send a thank-you DM to the attendee themselves as confirmation
                      const attendeeId = String(user.id).replace(/n$/, '');
                      if (attendeeId) {
                        const content = `Kiitos kun osallistuit tapahtumaan "${event.title || '—'}", jonka järjesti ${event.author || '—'}.\n\nArviosi: ${ratingValue} / 5${ratingReason ? `\nPerustelu: ${ratingReason}` : ''}`;
                        fetch(`${API}/api/direct-messages`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            userId: attendeeId,
                            fromUserId: attendeeId,
                            toUserId: attendeeId,
                            content,
                          }),
                        }).catch(() => {});
                      }
                      setShowRatingPrompt(false);
                    } catch (err) {
                      setRatingError('Arvion tallennus epäonnistui.');
                    } finally {
                      setSubmittingRating(false);
                    }
                  }}
                >
                  Lähetä arvio
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Chat Toggle Button — only render when chat is not open (in-page or external tab) */}
        {showDockableChat && !(isLivestreamEvent && isOwnEvent && authorMainView === 'chat') && !chatExpanded && !externalChatWindowOpen && (
          <button
            type="button"
            className={
              'EventLivestream-chat-toggle' +
              (isStreamEvent ? ' EventLivestream-chat-toggle--center' : '')
            }
            onClick={() => setChatExpanded(true)}
            aria-label="Open chat"
            title="Open chat"
          >
            <MessageCircle size={32} strokeWidth={2} />
          </button>
        )}

        {/* Chat Window */}
        {showDockableChat && !(isLivestreamEvent && isOwnEvent && authorMainView === 'chat') && chatExpanded && (
          <div
            ref={chatWindowRef}
            className={`EventLivestream-chat ${chatDocked ? 'EventLivestream-chat--docked' : ''}`}
            style={chatDocked ? { left: `${chatPosition.x}px`, top: `${chatPosition.y}px` } : {}}
          >
            <div 
              className="EventLivestream-chat-header" 
              onMouseDown={handleMouseDown}
              style={{ cursor: chatDocked ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
            >
              <span className="EventLivestream-chat-title">Event Chat</span>
              <div className="EventLivestream-chat-controls">
                <button
                  type="button"
                  className="EventLivestream-chat-dock"
                  onClick={() => {
                    if (typeof window !== 'undefined' && eventId) {
                      const chatUrl = `${window.location.origin}/event/${eventId}/chat`;
                      const features = 'noopener,noreferrer,width=480,height=720,resizable=yes,scrollbars=yes';
                      const w = window.open(chatUrl, 'eventChatWindow', features);
                      setExternalChatWindowOpen(true);
                      setChatExpanded(false);
                      setChatDocked(false);
                      if (w) {
                        externalChatWindowRef.current = w;
                      } else {
                        // Popup may have been blocked; show circle again after a delay so user can retry
                        setTimeout(() => setExternalChatWindowOpen(false), 2000);
                      }
                    }
                  }}
                  aria-label="Open chat in new window"
                  title="Open chat in new window"
                >
                  <Maximize2 size={20} />
                </button>
                <button
                  type="button"
                  className="EventLivestream-chat-close"
                  onClick={() => {
                    setChatExpanded(false);
                    setChatDocked(false);
                  }}
                  aria-label="Close chat"
                  title="Close chat"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="EventLivestream-chat-messages">
              <ChatMessages
                messages={[...chatMessages, ...slashResponses]}
                editingMessageId={editingMessageId}
                editingDraft={editingDraft}
                setEditingDraft={setEditingDraft}
                editInputRef={editInputRef}
                onStartEdit={handleStartEditOwnMessage}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onDeleteMessage={handleDeleteOwnMessage}
                onReply={handleReplyToMessage}
                onEditInputKeyDown={handleEditInputKeyDown}
                currentUserDisplayName={user?.displayName || user?.username}
                profileRedirectPath={`/event/stream/${eventId}`}
                mutedAuthorKeys={mutedAuthorKeys}
                onToggleMuteAuthor={handleToggleMuteAuthor}
                onDismissMessage={handleDismissSlashMessage}
                autoScroll={false}
                scrollToEndTrigger={scrollTrigger}
              />
            </div>
            <ChatInput
              inputRef={chatInputRef}
              value={chatInput}
              onChangeValue={setChatInput}
              onSubmit={handleSendChat}
              lastSentMessage={lastSentMessage}
              placeholder="Kirjoita viesti..."
              maxLength={500}
              replyingTo={replyingToMessage}
              onCancelReply={handleCancelReply}
              mentionCandidates={Array.from(
                new Set([
                  'pulinabotti',
                  ...(chatMessages || []).map((m) => m.authorName).filter(Boolean),
                ])
              )}
              kuiskaaCandidates={kuiskaaCandidates}
              currentUser={user}
              leftSlot={!isLevyraatiEvent ? <ChatInlinePlayer /> : null}
            />
          </div>
        )}
      </div>
    </div>
  );
}
